import {Table, TableActionsCell, Typography, nodeColumn} from '@sollapay/ui/components';
import {formatRelativeTimeLocalized} from '@sollapay/utils';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {DateCell, PersonCell} from '@/components';

import {isSystemAccessMember} from '../utils';
import {ProtectedRoleBadge} from './ProtectedRoleBadge';
import {TeamMemberMoreMenu, getTeamMemberMenuActions} from './TeamMemberMoreMenu';
import {TeamMemberRoleSelect} from './TeamMemberRoleSelect';
import {TeamMemberStatusBadge} from './TeamMemberStatusBadge';
import {TeamSystemAccessGroupHeader} from './TeamSystemAccessGroupHeader';

import type {FirmMemberDTO} from '@sollapay/types';
import type {ListOnSortingChange, ListSortingState} from '@sollapay/ui/components';
import type {FC} from 'react';

/** Single source of truth for the table's two-group split — see `isSystemAccessMember`. */
const getMemberGroupKey = (row: FirmMemberDTO): 'members' | 'system' =>
  isSystemAccessMember(row) ? 'system' : 'members';

export interface TeamMemberTableProps {
  rows: FirmMemberDTO[];
  sorting: ListSortingState;
  onSortingChange: ListOnSortingChange;
  onRemove: (member: FirmMemberDTO) => void;
  /** From `Permission.TEAM_MANAGE_MEMBERS` — gates the role select and the remove action. */
  canManageMembers: boolean;
}

export const TeamMemberTable: FC<TeamMemberTableProps> = ({
  rows,
  sorting,
  onSortingChange,
  onRemove,
  canManageMembers
}) => {
  const {t, i18n} = useTranslation(['team', 'common']);

  const columns = useMemo(
    () => [
      nodeColumn<FirmMemberDTO>({
        id: 'member',
        header: t('columns.member'),
        accessorFn: row => row.name ?? row.email,
        cell: row => (
          <PersonCell
            name={row.name ?? row.email}
            initialsSource={row.name ?? row.email}
            subtitle={row.name ? row.email : undefined}
            avatarUrl={row.picture}
            seed={row.id}
            emptyLabel={row.email}
            size="lg"
          />
        )
      }),
      nodeColumn<FirmMemberDTO>({
        id: 'role',
        header: t('columns.role'),
        accessorFn: row => row.roleKey,
        cell: row =>
          isSystemAccessMember(row) ? (
            <ProtectedRoleBadge roleKey={row.roleKey} />
          ) : (
            <TeamMemberRoleSelect
              memberId={row.id}
              roleKey={row.roleKey}
              readOnly={!canManageMembers}
            />
          )
      }),
      nodeColumn<FirmMemberDTO>({
        id: 'status',
        header: t('columns.status'),
        accessorFn: row => row.status,
        cell: row => <TeamMemberStatusBadge status={row.status} />
      }),
      nodeColumn<FirmMemberDTO>({
        id: 'createdAt',
        header: t('columns.createdAt'),
        accessorFn: row => row.createdAt,
        cell: row => <DateCell date={row.createdAt} />
      }),
      nodeColumn<FirmMemberDTO>({
        id: 'lastLoginAt',
        header: t('columns.lastLoginAt'),
        accessorFn: row => row.lastLoginAt ?? '',
        cell: row => (
          <Typography size="sm" color="tertiary">
            {row.lastLoginAt ? formatRelativeTimeLocalized(row.lastLoginAt, i18n.language) : '—'}
          </Typography>
        )
      }),
      nodeColumn<FirmMemberDTO>({
        id: 'actions',
        header: '',
        width: 'min',
        enableSorting: false,
        cell: row => (
          <TableActionsCell>
            <TeamMemberMoreMenu
              actions={getTeamMemberMenuActions(row.status)}
              status={row.status}
              onRemove={
                canManageMembers && !isSystemAccessMember(row) ? () => onRemove(row) : undefined
              }
            />
          </TableActionsCell>
        )
      })
    ],
    [t, i18n.language, canManageMembers, onRemove]
  );

  return (
    <Table
      columns={columns}
      data={rows}
      sorting={sorting}
      onSortingChange={onSortingChange}
      manualSorting
      emptyStateText={t('common:noResults')}
      isRowPinned={row => getMemberGroupKey(row) === 'members'}
      getGroupKey={getMemberGroupKey}
      renderGroupHeader={row =>
        getMemberGroupKey(row) === 'system' ? <TeamSystemAccessGroupHeader /> : null
      }
    />
  );
};
